/**
 * Setup › Products (CU-869fhuhy6, brief v3, UX v3 2026-10-20). Replaces
 * the Rate cards (read-only until the migration is validated, UX D23).
 *
 * A product = a name + 1..N configuration rows:
 *   site → read pricelist → markup cap % → write pricelist (→ derived pricelists ± delta)
 * Each row may say which of our offers the product is on that site
 * (tariff type, insurance); blank = any → the cheapest own row.
 *
 * On screen (UX v3 D13): a card per product. Its rows read in two halves
 * the header names — WHAT WE SELL (site · our offer there) and WHERE THE
 * ENGINE READS AND WRITES IT (reads · cap · writes → derived) — and under
 * each row the sentence generated from it: "On DiscoverCars, our Prepaid ·
 * Zero excess reads X, is capped at 45 % and writes Y". A saved card is
 * read, not edited: "Edit" puts the same card in form mode in place. A new
 * card is born at the top, in form mode, with Discard. Saving is a button
 * because the save-time check needs the whole card (two products sharing
 * a site must have distinguishable kinds — the guilty row is marked), and
 * because the server's refusal (409 / 422) is painted inside the card with
 * its own words (D15.3) — `useProducts`, one request per mutation.
 */
import React, { useEffect, useMemo, useState } from 'react'
import {
	Autocomplete, Box, Button, Chip, FormControl, IconButton,
	InputLabel, Link, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import {
	AppBulkEditAction, AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader, AppNote,
	AppToolbarCreateButton, AppValueChip, secondaryButtonSx,
} from '@components'
import { dsColorNew } from '@palette'
import { alpha } from '@mui/material/styles'
import {
	Add as AddIcon, DeleteOutline as DeleteIcon, EditOutlined as EditIcon, SubdirectoryArrowRight as SubdirIcon,
	LayersOutlined as LayersIcon, InfoOutlined as InfoIcon,
} from 'Pages/DsRepricing/@components/icons'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import { Surface, SurfaceHeader, EmptyState, LoadingState, HelpNote } from 'Pages/DsRepricing/Setup/@components/Surface'
import { inputSx, codeSx, headCellSx, cellPadSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import UnsavedChip from 'Pages/DsRepricing/Setup/@components/UnsavedChip'
import TierEditor from 'Pages/DsRepricing/Setup/@components/TierEditor'
import useConfirm from 'Pages/DsRepricing/@components/useConfirm'
import useToast from '@hooks/useToast'
import { usePricelistCatalog, usePricelistTiers } from 'Pages/DsRepricing/Setup/@services/pricelists'
import { mergeTiers } from 'Pages/DsRepricing/Setup/@services/tiers'
import { SiteCatalogProvider, useSiteCatalog } from 'Pages/DsRepricing/Setup/@services/sites'
import { useAxisCatalog, PREPAID_OPTIONS, prepaidKey, prepaidValue } from 'Pages/DsRepricing/Setup/@services/axes'
import { useProducts } from 'Pages/DsRepricing/Setup/@services/useProducts'
import { strategiesApi } from 'Pages/DsRepricing/Page/@services/api'
import { fromServer as strategyFromServer } from 'Pages/DsRepricing/Page/@services/adapters'
import {
	productConflicts, conflictSentence, conflictOwnRowId, productRowIssues, writeExclusion, linkedWriteSentence, kindLabel, fmtDelta,
	productSites, usedBy, pricedBy, pricelistUsers, pricelistClashes, clashHint, rowSentence, kindSummary, tiersDiffer, serverProblems, REFUSED_IN_USE_CODES,
	rowRemoval, sharedInProduct, sharedNote, sharedTiersDiffer, writeDeltaOf,
} from 'Pages/DsRepricing/Setup/@services/products'

export default function ProductsTab() {
	return <SiteCatalogProvider><Inner/></SiteCatalogProvider>
}

let seq = 0
const localId = (prefix) => `${prefix}_${Date.now().toString(36)}${(++seq).toString(36)}`
const newRow = () => ({ id: localId('tmp'), siteId: null, readPricelistId: null, capPct: null, writePricelistId: null, derived: [], tariffType: null, insuranceId: null, tierConfig: [] })
const newProduct = () => ({ id: localId('new'), name: '', renameHint: false, rows: [newRow()], _new: true })
const strategyHref = (id) => `${DS_REPRICING_ROUTES.STRATEGIES_PATH}?rule=${id}`
const KIND_TITLE = 'Leave it blank if this product is every offer we sell on this site; the engine takes the cheapest own row in each cell.'
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`
// A link in the app's blue
const linkSx = { color: dsColorNew.appBlue, fontWeight: 600, textDecorationColor: 'transparent', '&:hover': { textDecorationColor: dsColorNew.appBlue } }
// Small muted lines under a name, a code, a row
const subSx = { fontSize: 12, color: dsColorNew.textMuted }
// The card's head is the app's navy bar (the one of the table heads and the selection bar): the product
// name in white, its summary in muted white, Edit / Discard / Delete as ghost buttons on navy
// (`AppSelectionBarButton`) — only Save keeps the blue (Curro, 2026-10-20).
const headSubSx = { fontSize: 12, color: alpha(dsColorNew.white, 0.72) }
const ghostOnNavySx = {
	...secondaryButtonSx, color: alpha(dsColorNew.white, 0.88), backgroundColor: 'transparent',
	border: `1px solid ${alpha(dsColorNew.white, 0.35)}`,
	'&:hover': { borderColor: dsColorNew.white, backgroundColor: alpha(dsColorNew.white, 0.1), boxShadow: 'none' },
	'&.Mui-disabled': { color: alpha(dsColorNew.white, 0.4), borderColor: alpha(dsColorNew.white, 0.2) },
}
const deleteOnNavySx = { color: alpha(dsColorNew.white, 0.6), '&:hover': { color: dsColorNew.dangerBorder, bgcolor: alpha(dsColorNew.white, 0.1) } }

/** The strategies of the tenant, once, for "used by" and "priced by" (criterion 6). */
function useStrategies() {
	const [state, setState] = useState({ items: [], loading: true })
	useEffect(() => {
		let alive = true
		strategiesApi.list()
			.then(rows => alive && setState({ items: rows.map(strategyFromServer), loading: false }))
			.catch(() => alive && setState({ items: [], loading: false }))
		return () => { alive = false }
	}, [])
	return state
}

function Inner() {
	const { items: pricelists, loading, error, reload: reloadCatalog } = usePricelistCatalog()
	const products = useProducts()
	const saved = products.items
	const { items: strategies } = useStrategies()
	const sites = useSiteCatalog()
	const axes = useAxisCatalog()
	const { showToast } = useToast()
	const [confirm, confirmDialog] = useConfirm()
	const [drafts, setDrafts] = useState({})     // productId → draft (being edited, or new)
	const [errors, setErrors] = useState({})     // productId → { messages: [sentence], rowIds: [rowId], refused: bool }
	const [saving, setSaving] = useState({})     // productId → true while its request is in flight
	const [refused, setRefused] = useState(null) // { title, body, strategies } — the dialog with no red button (D16-c)

	const byId = useMemo(() => new Map(pricelists.map(p => [String(p.id), p])), [pricelists])
	// New cards first (D13: a new card is born at the top), then the saved ones with their draft applied.
	const cards = [...Object.values(drafts).filter(d => d._new), ...saved.map(p => drafts[p.id] || p)]
	const siteCount = new Set(saved.flatMap(productSites)).size
	const rowCount = saved.reduce((n, p) => n + (p.rows || []).length, 0)

	const edit = (draft) => setDrafts(d => ({ ...d, [draft.id]: draft }))
	const clearErrors = (id) => setErrors(e => { const n = { ...e }; delete n[id]; return n })
	const discard = (id) => { setDrafts(d => { const n = { ...d }; delete n[id]; return n }); clearErrors(id) }
	const add = () => edit(newProduct())
	const usersOf = (p) => (p.usedBy?.length ? p.usedBy.map(u => ({ ...u, ...(strategies.find(s => String(s.id) === String(u.id)) || {}) })) : usedBy(p.id, strategies, null))

	// The first net is the front's (same rules as the server); the second is the server's 409 / 422,
	// painted with the same sentences and the guilty row marked (D15.3).
	const save = async (draft) => {
		const others = saved.filter(p => p.id !== draft.id)
		const problems = productRowIssues.detailed(draft, saved, pricelists).map(i => ({ text: i.text, rowId: i.rowId }))
		for (const r of draft.rows || []) {
			const why = linkedWriteSentence(byId.get(String(r.writePricelistId)), pricelists)
			if (why) problems.push({ text: why, rowId: r.id })
		}
		for (const c of productConflicts([...others, draft])) {
			const rowId = conflictOwnRowId(c, draft.id)
			if (rowId != null) problems.push({ text: conflictSentence(c, sites.labelOf, axes, draft.id), rowId })
		}
		// One pricelist, one product (CU-869fm5juc, criterion 11): the server says the same (409).
		problems.push(...pricelistClashes(draft, others, pricelists, sites.labelOf))
		if (problems.length) {
			setErrors(e => ({ ...e, [draft.id]: { messages: problems.map(p => p.text), rowIds: problems.map(p => p.rowId).filter(id => id != null) } }))
			return
		}
		setSaving(s => ({ ...s, [draft.id]: true }))
		try {
			const { _new, ...clean } = draft
			const result = await products.save({ ...clean, name: clean.name.trim(), renameHint: false })
			discard(draft.id)
			showToast.success(`"${result.name}" saved.`)
		} catch (e) {
			if (REFUSED_IN_USE_CODES.includes(e?.code)) {
				setRefused({ title: `"${draft.name}" cannot be saved like this`, body: e.message, strategies: e.details?.strategies || [] })
			} else {
				const list = serverProblems(e, sites.labelOf, axes)
				const messages = list.length ? list.map(p => p.text) : [e?.message || 'The server refused the product.']
				setErrors(x => ({ ...x, [draft.id]: { messages, rowIds: list.map(p => p.rowId).filter(id => id != null), refused: true } }))
			}
		} finally {
			setSaving(s => { const n = { ...s }; delete n[draft.id]; return n })
		}
	}

	// Criterion 7 (D16-c): a product a strategy uses cannot be deleted — a dialog with the list and one Close button.
	// The server says the same (409 `product_in_use`) if the list on screen is stale.
	const remove = async (p) => {
		if (p._new) { discard(p.id); return }
		const users = usersOf(p)
		const refuse = (list) => setRefused({
			title: `"${p.name}" cannot be deleted`,
			body: `${list.length === 1 ? 'A strategy prices' : `${list.length} strategies price`} this product. Pick another product in ${list.length === 1 ? 'it' : 'each of them'} first, or delete ${list.length === 1 ? 'it' : 'them'}; then the product can go.`,
			strategies: list,
		})
		if (users.length) { refuse(users); return }
		if (!await confirm({
			title: `Delete the product "${p.name}"?`,
			body: 'No strategy uses it. Its rows (sites, pricelists, caps) go with it; nothing is deleted in the PMS.',
			confirmLabel: 'Delete product', destructive: true,
		})) return
		try {
			await products.remove(p.id)
			discard(p.id)
		} catch (e) {
			if (e?.code === 'product_in_use') refuse(e.details?.strategies || [])
			else showToast.error(e?.message || 'Could not delete the product')
		}
	}

	// Criteria 8-9 (CU-869fkr1a9; TL C2): a row with a site asks, naming the strategies that lose it and what they
	// keep; it is refused only when a strategy would be left with none of its sites — judged on every site this
	// edit removes, the rows already removed in the draft included. An empty row goes without a question. The change
	// is kept in the draft and travels with the save (the server checks the same rule, 409 `row_in_use`).
	const removeRow = async (p, r) => {
		const rows = p.rows || []
		const drop = () => edit({ ...p, rows: rows.filter(x => x.id !== r.id) })
		if (r.siteId == null) { drop(); return }
		const site = sites.labelOf(r.siteId)
		const names = (ids) => ids.map(sites.labelOf).join(', ')
		const { only, among, allSites } = rowRemoval(p, saved.find(x => x.id === p.id) || null, r.siteId, strategies)
		if (only.length) {
			setRefused({
				title: `The ${site} row cannot be removed`,
				body: only.length === 1
					? `${site} is the only site this strategy prices on "${p.name}": without the row it would have no site left. Give it another site, or delete it; then the row can go.`
					: `${site} is the only site these ${only.length} strategies price on "${p.name}": without the row they would have no site left. Give each of them another site, or delete them; then the row can go.`,
				strategies: only,
			})
			return
		}
		if (!await confirm({
			title: `Remove the ${site} row from "${p.name || 'this product'}"?`,
			body: `The product stops reading and writing on ${site}. Nothing is deleted in the PMS; the change is kept when you save the product.`,
			detail: (among.length || allSites.length) ? (
				<>
					{among.length > 0 && (
						<>
							<Box sx={{ color: dsColorNew.textDark }}><strong>{among.length === 1 ? '1 strategy loses' : `${among.length} strategies lose`} {names([...new Set(among.flatMap(s => s.loses))])}</strong> from the sites it chose:</Box>
							<Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, color: dsColorNew.textDark }}>
								{among.map(s => (
									<li key={s.id}>
										<Link href={strategyHref(s.id)} target="_blank" rel="noopener noreferrer" sx={linkSx}>{s.name}</Link>
										<Box component="span" sx={{ color: dsColorNew.textMuted }}> · priority {s.priority ?? '—'} · keeps {names(s.keeps)}</Box>
									</li>
								))}
							</Box>
						</>
					)}
					{allSites.length > 0 && (
						<Box sx={{ mt: among.length ? 1 : 0, color: dsColorNew.textMuted }}>
							Strategies with all the product's sites ({allSites.map(s => s.name).join(', ')}) keep running on the ones that remain.
						</Box>
					)}
				</>
			) : undefined,
			confirmLabel: 'Remove row', destructive: true,
		})) return
		drop()
	}

	const retryCatalog = () => (typeof reloadCatalog === 'function' ? reloadCatalog() : window.location.reload())

	return (
		<Surface>
			<SurfaceHeader
				icon="box-open"
				title="Products"
				subtitle={<>A product is <strong>one of our offers</strong>, configured per site: on each site, which pricelist the engine{' '}
					<em>reads</em> the current price from, how far the broker's markup may go (<em>cap</em>), which pricelist it <em>writes</em>,
					and the pricelists <em>derived</em> from that write (± delta). Optionally, which of our rows the product is on that site
					(tariff type, insurance) — blank means any, the cheapest own row. A strategy picks <strong>one product</strong>;
					its sites are the ones listed here.</>}
				actions={<>
					<Kpi label="Products" value={saved.length}/>
					<Kpi label="Rows" value={rowCount}/>
					<Kpi label="Sites" value={siteCount}/>
					<AppToolbarCreateButton divider={false} label="Add product" icon="plus" onClick={add}
					                        disabled={!!error || !!products.error} sx={{ height: 38, ml: 1 }}/>
				</>}/>

			{error ? (
				<AppNote severity="error" sx={{ mb: 2 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
						<span>Could not load the PMS pricelists — the products cannot be edited until they load.</span>
						<Button size="small" variant="outlined" onClick={retryCatalog} sx={{ ...secondaryButtonSx, py: 0.25 }}>Retry</Button>
					</Box>
				</AppNote>
			) : products.error ? (
				<AppNote severity="error" sx={{ mb: 2 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
						<span>Could not load the products{products.error?.message ? ` — ${products.error.message}` : ''}.</span>
						<Button size="small" variant="outlined" onClick={() => products.reload()} sx={{ ...secondaryButtonSx, py: 0.25 }}>Retry</Button>
					</Box>
				</AppNote>
			) : loading || products.loading ? <LoadingState what={loading ? 'pricelists' : 'products'}/>
			: cards.length === 0 ? (
				<EmptyState title="No products yet"
				            description="Create the first product: give it a name and one row per site — which pricelist it reads, the markup cap and which pricelist it writes. A strategy cannot be saved without a product."
				            action={<Button variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }}/>} onClick={add} sx={{ ...secondaryButtonSx, height: 36 }}>Add product</Button>}/>
			) : (
				<Box sx={{ display: 'grid', gap: 2 }}>
					{cards.map(p => (
						<ProductCard key={p.id} product={p} savedVersion={saved.find(x => x.id === p.id) || null}
						             editing={!!drafts[p.id]} errors={errors[p.id] || null} saving={!!saving[p.id]}
						             pricelists={pricelists} byId={byId} allProducts={[...saved.filter(x => x.id !== p.id), p]}
						             users={usersOf(p)} strategies={strategies}
						             onChange={edit} onEdit={() => edit(p)} onSave={() => save(p)} onDiscard={() => discard(p.id)}
						             onRemove={() => remove(p)} onRemoveRow={(r) => removeRow(p, r)}/>
					))}
				</Box>
			)}

			<HelpNote sx={{ mt: 2 }}>
				Two products may share a site only if their kinds differ there (tariff type, insurance). <strong>One pricelist, one
				product:</strong> a pricelist written or derived by one product cannot be written or derived by another — if one product's
				price follows another's, link its pricelist in the PMS with a % or a fixed value instead. Reading the same pricelist from
				several products is fine. Rows of one product may write the same pricelist, and a row's write may be derived by another
				row of it: the pricelist is sent once per cell, with the lowest value. The write of a row may carry a delta (± % or €): use
				it to write the <strong>base</strong> of pricelists the PMS keeps linked — the linked ones inherit it. A pricelist the PMS
				keeps linked to a base can never be written. Price limits bound the decided net, before the write's delta. A product keeps
				at least one row.
			</HelpNote>
			{confirmDialog}
			<RefusedDialog refused={refused} onClose={() => setRefused(null)}/>
		</Surface>
	)
}

/** The refusal of criterion 7 / 10: title, why, the strategies (each opens in the wizard), one Close button — no red one. */
function RefusedDialog({ refused, onClose }) {
	if (!refused) return null
	return (
		<AppDialog open onClose={onClose} maxWidth="xs">
			<AppDialogHeader icon="ban" title={refused.title} onClose={onClose}/>
			<AppDialogBody sx={dialogBodySx}>
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, lineHeight: 1.55 }}>{refused.body}</Typography>
				{refused.strategies.length > 0 && (
					<Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 2.5, fontSize: 13, color: dsColorNew.textDark }}>
						{refused.strategies.map(s => (
							<li key={s.id}>
								<Link href={strategyHref(s.id)} target="_blank" rel="noopener noreferrer" sx={linkSx}
								      title="Opens the strategy in the wizard, step 1">{s.name}</Link>
								<Box component="span" sx={{ color: dsColorNew.textMuted }}> · priority {s.priority ?? '—'}{s.enabled === false || s.status === 'disabled' || s.status === 'draft' ? ' · off' : ''}</Box>
							</li>
						))}
					</Box>
				)}
			</AppDialogBody>
			<AppDialogFooter>
				{/* No red button on purpose (D16-c): there is nothing to confirm, only something to read */}
				<AppDialogCancel onClick={onClose}>Close</AppDialogCancel>
			</AppDialogFooter>
		</AppDialog>
	)
}

function ProductCard({ product: p, savedVersion, editing, errors, saving, pricelists, byId, allProducts, users, strategies,
                       onChange, onEdit, onSave, onDiscard, onRemove, onRemoveRow }) {
	const sites = useSiteCatalog()
	const axes = useAxisCatalog()
	const rows = p.rows || []
	const isNew = !!p._new
	const changed = isNew || !savedVersion || JSON.stringify(p) !== JSON.stringify(savedVersion)
	const messages = errors?.messages || []
	const badRows = new Set(errors?.rowIds || [])
	const setRows = (next) => onChange({ ...p, rows: next })
	const setRow = (id, patch) => setRows(rows.map(r => (r.id === id ? { ...r, ...patch } : r)))
	const col = (w) => ({ ...cellPadSx, width: w, verticalAlign: 'top' })
	const summary = kindSummary(p, axes, sites.labelOf)
	// A pricelist written or derived by more than one row of this product is sent once, the lowest value (CU-869fkr1a9).
	const shared = sharedInProduct(p)
	const noteFor = (pricelistId) => {
		if (pricelistId == null) return null
		return sharedNote(shared.get(String(pricelistId)), byId.get(String(pricelistId))?.code || '?', sharedTiersDiffer(p, pricelistId, pricelists))
	}
	// The two halves of the header (D13) as a light group line over a grey column head: the theme paints
	// every `th` navy and white, so both set their background and their text colour. The navy is the
	// card's head and frame; the rows' head is discreet so the product reads as one block (Curro, 2026-10-20).
	const groupHeadSx = { bgcolor: dsColorNew.tableRowAlt, color: dsColorNew.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: 0.6,
	                      textTransform: 'uppercase', py: 0.5, borderBottom: `1px solid ${dsColorNew.grayBorder}`, borderRadius: 0 }
	const colHeadSx = { ...headCellSx, bgcolor: dsColorNew.pillTrackBg, color: dsColorNew.textDark, fontWeight: 600,
	                    borderBottom: `1px solid ${dsColorNew.grayBorder}`, borderRadius: 0 }
	return (
		<Box sx={{ bgcolor: dsColorNew.white, borderRadius: '14px', overflow: 'hidden',
		           border: `2px solid ${messages.length ? dsColorNew.dangerRed : dsColorNew.tableHead}` }}>
			<Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', px: 2, py: 1.5, bgcolor: dsColorNew.tableHead, flexWrap: 'wrap' }}>
				{editing ? (
					<TextField size="small" value={p.name || ''} placeholder="Product name" autoFocus={isNew} sx={{ ...inputSx, width: 300, bgcolor: dsColorNew.white, borderRadius: '8px' }}
					           error={messages.some(m => /^Give the product a name|^Another product is already called/.test(m))}
					           onChange={e => onChange({ ...p, name: e.target.value, renameHint: false })}
					           inputProps={{ style: { fontWeight: 600 } }}/>
				) : (
					<Typography sx={{ fontWeight: 600, fontSize: 15, color: dsColorNew.white }}>{p.name || <em>unnamed</em>}</Typography>
				)}
				{p.renameHint && (
					<Chip size="small" label="rename me — named by the migration"
					      title="This name is the kind of the old rate cards, which had no name. Give it the one you use for this offer."
					      sx={toneChipSx('warn')}/>
				)}
				<Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
					<Typography sx={headSubSx}>
						{plural(rows.length, 'row')} · {plural(productSites(p).length, 'site')}
						{' · '}{users.length ? `used by ${users.map(u => u.name).join(', ')}` : 'used by no strategy yet'}
					</Typography>
					{!editing && summary && (
						<Typography sx={headSubSx} title="Our offer on each site — what decides whether two products clash">
							{summary}
						</Typography>
					)}
				</Box>
				<Box sx={{ flex: 1 }}/>
				{editing && (
					<>
						<UnsavedChip refused={errors?.refused ? { message: messages[0] } : undefined}
						             reason={changed && !errors?.refused ? (isNew ? 'New product — fill its rows and save' : 'Edited — save to keep') : null}/>
						<AppBulkEditAction onClick={onSave} disabled={saving} sx={{ height: 34 }}>{saving ? 'Saving…' : 'Save product'}</AppBulkEditAction>
						<Button size="small" variant="outlined" onClick={onDiscard} disabled={saving} sx={{ ...ghostOnNavySx, height: 34 }}>Discard</Button>
					</>
				)}
				{!editing && (
					<Button size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: 14 }}/>} onClick={onEdit} sx={{ ...ghostOnNavySx, height: 34 }}>Edit</Button>
				)}
				{!isNew && (
					<IconButton size="small" onClick={onRemove} title="Delete product" sx={deleteOnNavySx}>
						<DeleteIcon fontSize="small"/>
					</IconButton>
				)}
			</Box>

			{messages.length > 0 && (
				<AppNote severity="error" sx={{ borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderTop: 'none' }}>
					<strong>Not saved.</strong>
					<Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>{messages.map((e, i) => <li key={i}>{e}</li>)}</Box>
				</AppNote>
			)}

			<Table size="small" sx={{ '& td': { fontSize: 13 } }}>
				<TableHead>
					{/* D13: two halves the header names — what we sell, and where the engine reads and writes it. */}
					<TableRow>
						<TableCell colSpan={2} sx={groupHeadSx}>What we sell</TableCell>
						<TableCell colSpan={3} sx={{ ...groupHeadSx, borderLeft: `1px solid ${dsColorNew.grayBorder}` }}>Where the engine reads and writes it</TableCell>
						<TableCell sx={groupHeadSx}/>
					</TableRow>
					<TableRow sx={{ '& th': colHeadSx }}>
						<TableCell>Site</TableCell>
						<TableCell>Our offer there <Box component="span" sx={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</Box></TableCell>
						<TableCell>Reads</TableCell>
						<TableCell>Markup cap</TableCell>
						<TableCell>Writes (± delta) → derived</TableCell>
						<TableCell align="right"/>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((r, i) => {
						const write = byId.get(String(r.writePricelistId))
						const read = byId.get(String(r.readPricelistId))
						const derived = r.derived || []
						const bad = badRows.has(r.id)
						const priced = isNew ? [] : pricedBy(p.id, r.siteId, strategies)
						// One pricelist, one product (CU-869fm5juc): who else writes or derives this row's pricelists.
						const users = pricelistUsers(r, p, allProducts)
						const writeHints = users.write.map(u => clashHint('write', write?.code, u))
						const derivedHints = (pid) => (users.derived[pid] || []).map(u => clashHint('derived', byId.get(String(pid))?.code || '?', u))
						const zebra = i % 2 === 1 ? dsColorNew.tableRowAlt : 'transparent'
						const firstCell = { ...col(230), borderLeft: '3px solid', borderLeftColor: bad ? dsColorNew.dangerRed : 'transparent' }
						const kindText = kindLabel(r, axes)
						return (
							<React.Fragment key={r.id}>
								<TableRow sx={{ bgcolor: bad ? dsColorNew.dangerBg : zebra, '& td': { borderBottom: 'none' } }}>
									<TableCell sx={firstCell}>
										{editing ? (
											<Autocomplete size="small" options={sites.options} value={sites.options.find(o => o.id === Number(r.siteId)) || null}
											              getOptionLabel={(o) => o.name} isOptionEqualToValue={(a, b) => a.id === b.id}
											              onChange={(_, o) => setRow(r.id, { siteId: o?.id ?? null })}
											              renderInput={(params) => <TextField {...params} placeholder={sites.loading ? 'Loading sites…' : 'Site'} error={bad && r.siteId == null} sx={inputSx}/>}/>
										) : (
											<Typography sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{r.siteId == null ? <em>no site</em> : sites.labelOf(r.siteId)}</Typography>
										)}
										{priced.length > 0 && (
											<Typography sx={{ ...subSx, mt: 0.5 }} title="The strategies on this product whose site scope covers this site">
												priced by {priced.map(s => `${s.name} (${s.priority ?? '—'})`).join(', ')}
											</Typography>
										)}
									</TableCell>
									<TableCell sx={col(300)}>
										{editing ? (
											<>
												<Box sx={{ display: 'flex', gap: 1 }}>
													<FormControl size="small" sx={{ ...inputSx, width: 140 }} error={bad} title={KIND_TITLE}>
														<InputLabel shrink>Tariff type</InputLabel>
														<Select label="Tariff type" notched displayEmpty value={prepaidKey(r.tariffType)}
														        onChange={e => setRow(r.id, { tariffType: prepaidValue(e.target.value) })}>
															<MenuItem value=""><em>Any</em></MenuItem>
															{PREPAID_OPTIONS.map(o => <MenuItem key={o.code} value={o.code}>{o.name}</MenuItem>)}
														</Select>
													</FormControl>
													<Autocomplete size="small" options={axes.insurances} sx={{ flex: 1, minWidth: 140 }} title={KIND_TITLE}
													              value={axes.insurances.find(o => o.code === String(r.insuranceId)) || null}
													              getOptionLabel={(o) => o.name} isOptionEqualToValue={(a, b) => a.code === b.code}
													              onChange={(_, o) => setRow(r.id, { insuranceId: o ? Number(o.code) : null })}
													              renderInput={(params) => <TextField {...params} label="Insurance" InputLabelProps={{ shrink: true }} error={bad}
													                                                 placeholder={axes.loading ? 'Loading…' : axes.insurances.length ? 'Any' : 'No catalogue'} sx={inputSx}/>}/>
												</Box>
												<Typography sx={{ fontSize: 12, color: bad ? dsColorNew.dangerRed : dsColorNew.textMuted, mt: 0.5 }}>{kindText}{kindText === 'any offer' ? ' → cheapest own row' : ''}</Typography>
											</>
										) : (
											<Typography sx={{ color: bad ? dsColorNew.dangerRed : dsColorNew.textDark, fontWeight: bad ? 600 : 400 }}>
												{kindText}{kindText === 'any offer' ? <Box component="span" sx={{ color: dsColorNew.textMuted }}> → cheapest own row</Box> : ''}
											</Typography>
										)}
									</TableCell>
									<TableCell sx={{ ...col(230), borderLeft: `1px solid ${dsColorNew.grayBorder}` }}>
										{editing ? (
											<PlPicker value={r.readPricelistId} pricelists={pricelists} placeholder="pricelist it reads" error={bad && r.readPricelistId == null}
											          onChange={(id) => setRow(r.id, { readPricelistId: id })}/>
										) : (
											<ReadOnlyPl pl={read} missing="no read pricelist yet"/>
										)}
										<Brackets read={read} write={write} tierConfig={r.tierConfig || []} editable={editing}
										          onChange={(tierConfig) => setRow(r.id, { tierConfig })}/>
									</TableCell>
									<TableCell sx={col(120)}>
										{editing ? (
											<TextField type="number" size="small" value={r.capPct ?? ''} placeholder="no cap"
											           onChange={(e) => setRow(r.id, { capPct: e.target.value === '' ? null : Number(e.target.value) })}
											           InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textMuted }}>%</Typography> }}
											           inputProps={{ style: { width: 52, textAlign: 'right', fontWeight: 600 } }}
											           sx={inputSx}/>
										) : (
											<Typography sx={{ fontWeight: r.capPct != null ? 600 : 400, color: dsColorNew.textDark }}>{r.capPct != null ? `${r.capPct} %` : <Box component="em" sx={{ fontWeight: 400, color: dsColorNew.textMuted }}>no cap</Box>}</Typography>
										)}
									</TableCell>
									<TableCell sx={{ ...cellPadSx, verticalAlign: 'top' }}>
										{editing ? (
											<>
												<Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
													<Box sx={{ flex: 1, minWidth: 160 }}>
														<PlPicker value={r.writePricelistId} pricelists={pricelists} placeholder="pricelist it writes" error={bad && r.writePricelistId == null}
														          disabledReason={(pl) => writeExclusion(pl, pricelists)}
														          /* D17: choosing the write with Reads empty fills Reads with the same pricelist (editable). */
														          onChange={(id) => setRow(r.id, { writePricelistId: id, readPricelistId: r.readPricelistId ?? id })}/>
													</Box>
													{/* The write's delta (criterion 6): the derived's control, born empty — no value, no delta (UX Obs. 1). */}
													<TextField type="number" size="small" value={r.writeDelta?.v ?? ''} placeholder="±0" title="Delta on the write: the decided net ± this, sent to the pricelist"
													           onChange={(e) => setRow(r.id, { writeDelta: { k: r.writeDelta?.k || 'pct', v: e.target.value === '' ? null : Number(e.target.value) } })}
													           inputProps={{ style: { width: 48, textAlign: 'right', fontWeight: 600 } }} sx={inputSx}/>
													<Button size="small" variant="outlined" sx={{ ...secondaryButtonSx, minWidth: 40, px: 0.5, height: 40 }}
													        onClick={() => setRow(r.id, { writeDelta: { k: (r.writeDelta?.k || 'pct') === 'pct' ? 'eur' : 'pct', v: r.writeDelta?.v ?? null } })}>
														{(r.writeDelta?.k || 'pct') === 'pct' ? '%' : '€'}
													</Button>
												</Box>
												<LinkedBaseHint write={write} pricelists={pricelists}/>
												<ClashHints hints={writeHints}/>
												<SharedNote note={noteFor(r.writePricelistId)}/>
												<Box sx={{ mt: 1, pl: 1, borderLeft: `2px solid ${dsColorNew.grayBorder}` }}>
													{derived.map(d => (
													<React.Fragment key={d.id}>
														<Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 0.75 }}>
															<SubdirIcon sx={{ fontSize: 16, color: dsColorNew.textSubtle }}/>
															<Box sx={{ flex: 1, minWidth: 160 }}>
																<PlPicker value={d.pricelistId} pricelists={pricelists} placeholder="derived pricelist"
																          disabledReason={(pl) => String(pl.id) === String(r.writePricelistId) ? 'the write pricelist itself'
																              : derived.some(x => x.id !== d.id && String(x.pricelistId) === String(pl.id)) ? 'already derived in this row'
																              : String(pl.id) === String(r.readPricelistId) ? 'the read pricelist'
																              : pl.base_pricelist_id ? 'linked in the PMS' : null}
																          onChange={(id) => setRow(r.id, { derived: derived.map(x => (x.id === d.id ? { ...x, pricelistId: id } : x)) })}/>
															</Box>
															<TextField type="number" size="small" value={d.delta?.v ?? 0}
															           onChange={(e) => setRow(r.id, { derived: derived.map(x => (x.id === d.id ? { ...x, delta: { k: d.delta?.k || 'pct', v: Number(e.target.value) || 0 } } : x)) })}
															           inputProps={{ style: { width: 48, textAlign: 'right', fontWeight: 600 } }} sx={inputSx}/>
															<Button size="small" variant="outlined" sx={{ ...secondaryButtonSx, minWidth: 40, px: 0.5 }}
															        onClick={() => setRow(r.id, { derived: derived.map(x => (x.id === d.id ? { ...x, delta: { k: (d.delta?.k || 'pct') === 'pct' ? 'eur' : 'pct', v: d.delta?.v ?? 0 } } : x)) })}>
																{(d.delta?.k || 'pct') === 'pct' ? '%' : '€'}
															</Button>
															<IconButton size="small" onClick={() => setRow(r.id, { derived: derived.filter(x => x.id !== d.id) })}
															            sx={{ color: dsColorNew.textMuted, '&:hover': { color: dsColorNew.dangerRed, bgcolor: dsColorNew.dangerBg } }}>
																<DeleteIcon fontSize="small"/>
															</IconButton>
														</Box>
														<ClashHints hints={derivedHints(d.pricelistId)} sx={{ mt: -0.25, mb: 0.75 }}/>
														<SharedNote note={noteFor(d.pricelistId)} sx={{ mt: -0.25, mb: 0.75 }}/>
													</React.Fragment>
													))}
													<Button size="small" startIcon={<AddIcon sx={{ fontSize: 12 }}/>} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, color: dsColorNew.appBlue }}
													        onClick={() => setRow(r.id, { derived: [...derived, { id: localId('tmp'), pricelistId: null, delta: { k: 'pct', v: 10 } }] })}>
														Add derived
													</Button>
												</Box>
											</>
										) : (
											<>
												<Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
													<ReadOnlyPl pl={write} missing="no write pricelist yet"/>
													{write && (writeDeltaOf(r)
														? <Chip size="small" label={fmtDelta(writeDeltaOf(r))} sx={{ ...toneChipSx('info'), fontWeight: 600 }} title="Delta on the write: the decided net ± this is sent"/>
														: <Typography sx={{ ...subSx, mt: 0.25 }}>no delta</Typography>)}
												</Box>
												<LinkedBaseHint write={write} pricelists={pricelists}/>
												<ClashHints hints={writeHints}/>
												<SharedNote note={noteFor(r.writePricelistId)}/>
												{derived.filter(d => d.pricelistId != null).map(d => (
													<React.Fragment key={d.id}>
														<Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5, ...subSx }}>
															<SubdirIcon sx={{ fontSize: 14, color: dsColorNew.textSubtle }}/>
															<Box component="span" sx={{ ...codeSx, fontSize: 11.5 }}>{byId.get(String(d.pricelistId))?.code || '?'}</Box>
															<span>{fmtDelta(d.delta)}</span>
														</Box>
														<ClashHints hints={derivedHints(d.pricelistId)}/>
														<SharedNote note={noteFor(d.pricelistId)}/>
													</React.Fragment>
												))}
											</>
										)}
									</TableCell>
									<TableCell sx={{ ...cellPadSx, verticalAlign: 'top', whiteSpace: 'nowrap' }} align="right">
										{editing && (
											<IconButton size="small" title={rows.length === 1 ? 'A product needs at least one row' : 'Remove row'} disabled={rows.length === 1}
											            onClick={() => onRemoveRow(r)} sx={{ color: dsColorNew.textMuted, '&:hover': { color: dsColorNew.dangerRed, bgcolor: dsColorNew.dangerBg } }}>
												<DeleteIcon fontSize="small"/>
											</IconButton>
										)}
										<Typography sx={{ fontSize: 11, color: dsColorNew.textSubtle }}>row {i + 1}</Typography>
									</TableCell>
								</TableRow>
								{/* The sentence the row generates (D13): the same words the wizard's picker shows. */}
								<TableRow sx={{ bgcolor: bad ? dsColorNew.dangerBg : zebra }}>
									<TableCell colSpan={6} sx={{ pt: 0, pb: 1, px: 1.5, borderLeft: '3px solid', borderLeftColor: bad ? dsColorNew.dangerRed : 'transparent' }}>
										<Typography sx={{ fontSize: 12.5, color: bad ? dsColorNew.dangerRed : dsColorNew.textMuted, fontStyle: 'italic' }}>
											{rowSentence(r, pricelists, axes, sites.labelOf)}
										</Typography>
									</TableCell>
								</TableRow>
							</React.Fragment>
						)
					})}
				</TableBody>
			</Table>
			{editing && (
				<Box sx={{ px: 2, py: 1, borderTop: `1px solid ${dsColorNew.grayBorder}`, display: 'flex', alignItems: 'center', gap: 2 }}>
					<Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }}/>} onClick={() => setRows([...rows, newRow()])} sx={{ ...secondaryButtonSx, height: 32 }}>
						Add row (another site)
					</Button>
					<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle }}>
						One row per site this product is sold on. Picking the pricelist it writes fills "Reads" with the same one — change it when the engine should read another.
					</Typography>
				</Box>
			)}
		</Box>
	)
}

/**
 * The info line of a pricelist several rows of the product write or derive
 * (UX Obs. 4: blue, inside the cell — not "also written by", which is
 * between products and stays grey), with the amber "brackets differ" part
 * when their brackets differ (TL Obs. 3). Never blocks Save.
 */
/** The red line under a picker whose pricelist another product writes or derives (CU-869fm5juc, plan D8). */
function ClashHints({ hints, sx }) {
	if (!hints?.length) return null
	return hints.map((h, i) => (
		<Typography key={i} sx={{ fontSize: 12, color: dsColorNew.dangerRed, mt: 0.5, ...sx }}>{h}</Typography>
	))
}

function SharedNote({ note, sx }) {
	if (!note) return null
	return (
		<Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mt: 0.75, px: 1, py: 0.75, borderRadius: '8px', fontSize: 12, lineHeight: 1.45,
		           bgcolor: dsColorNew.noteInfoBg, color: dsColorNew.noteInfo, border: `1px solid ${dsColorNew.noteInfoBorder}`, ...sx }}>
			<InfoIcon sx={{ fontSize: 13, mt: '2px' }}/>
			<Box component="span">
				{note.text}
				{note.warn && <Box component="span" sx={{ color: dsColorNew.noteAmber, fontWeight: 600 }}>{` — ${note.warn}`}</Box>}
			</Box>
		</Box>
	)
}

/** Under a write that is the base of pricelists the PMS keeps linked: why a delta here is useful (UX Obs. 1). */
function LinkedBaseHint({ write, pricelists }) {
	if (!write?.source_id) return null
	const n = (pricelists || []).filter(pl => pl.base_pricelist_id != null && String(pl.base_pricelist_id) === String(write.source_id)).length
	if (!n) return null
	return <Typography sx={{ ...subSx, mt: 0.25 }}>base of {plural(n, 'linked pricelist')} in the PMS</Typography>
}

/** A pricelist in read mode: the code as the app writes codes, the name under it; or what is missing. */
function ReadOnlyPl({ pl, missing }) {
	if (!pl) return <Typography sx={{ color: dsColorNew.noteAmber, fontStyle: 'italic', fontSize: 12.5 }}>{missing}</Typography>
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-start' }}>
			<Box component="span" sx={codeSx}>{pl.code}</Box>
			<Typography sx={subSx}>{pl.name}</Typography>
		</Box>
	)
}

/**
 * Criterion 3 (D16-a): the brackets of the read pricelist with their
 * reference LOR, folded under Reads — "N brackets · reference LOR 3 · 7 ·
 * 14 · 31" opens the `TierEditor` (same component as the rate cards had,
 * same warnings). A soft note when the write pricelist has other brackets.
 */
function Brackets({ read, write, tierConfig, editable, onChange }) {
	const [open, setOpen] = useState(false)
	const canonical = usePricelistTiers(read?.code)
	const merged = useMemo(() => mergeTiers(canonical, tierConfig || []), [canonical, tierConfig])
	if (!read) return null
	const live = merged.filter(t => t.status !== 'gone')
	const refs = live.map(t => t.reference_lor).join(' · ')
	const line = live.length ? `${plural(live.length, 'bracket')} · reference LOR ${refs}` : 'no brackets synced'
	const stale = merged.some(t => t.status !== 'ok')
	return (
		<Box sx={{ mt: 0.5 }}>
			<Box component={editable && live.length ? 'button' : 'span'} type={editable && live.length ? 'button' : undefined}
			     onClick={editable && live.length ? () => setOpen(o => !o) : undefined}
			     title={editable && live.length ? (open ? 'Fold the brackets' : 'Edit the reference LOR of each bracket') : undefined}
			     sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, p: 0, border: 'none', bgcolor: 'transparent', font: 'inherit',
			           fontSize: 11.5, color: stale ? dsColorNew.noteAmber : dsColorNew.textMuted, cursor: editable && live.length ? 'pointer' : 'default',
			           textDecoration: editable && live.length ? 'underline dotted' : 'none' }}>
				<LayersIcon sx={{ fontSize: 13 }}/>{line}{stale ? ' · review' : ''}
			</Box>
			{open && editable && (
				<Box sx={{ mt: 0.5, p: 1, bgcolor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '8px' }}>
					<TierEditor face={{ read_pricelist: read.code, tier_config: tierConfig || [] }} onChange={onChange}/>
				</Box>
			)}
			{tiersDiffer(read, write) && (
				<Typography sx={{ fontSize: 11.5, color: dsColorNew.noteAmber, mt: 0.25 }} title="The engine searches on the read pricelist's brackets; the write pricelist is written per bracket as the PMS has it.">
					the write pricelist has other brackets ({plural((write?.tiers || []).length, 'bracket')})
				</Typography>
			)}
		</Box>
	)
}

function PlPicker({ value, pricelists, onChange, disabledReason = () => null, allowEmpty = false, placeholder = 'Select…', error = false }) {
	const selected = pricelists.find(p => String(p.id) === String(value)) || null
	const why = selected ? disabledReason(selected) : null
	return (
		<Box>
			<Autocomplete size="small" options={pricelists} value={selected}
			              getOptionLabel={(o) => o.code}
			              isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
			              getOptionDisabled={(o) => !!disabledReason(o)}
			              disableClearable={!allowEmpty && value != null}
			              onChange={(_, next) => onChange(next?.id ?? null)}
			              // The list is wider than the cell: code + name on one line (Curro, 2026-10-20 —
			              // the popup took the input's width and the names broke into three lines).
			              componentsProps={{ popper: { placement: 'bottom-start',
			                                           sx: { width: 'auto !important', minWidth: 460, maxWidth: 640 } } }}
			              renderOption={(props, o) => {
				              const reason = disabledReason(o)
				              return (
					              <li {...props} key={o.id} title={reason || undefined}>
						              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
							              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
								              <Box component="span" sx={{ fontWeight: 600, fontFamily: 'inherit', fontSize: 13, minWidth: 160, whiteSpace: 'nowrap', color: dsColorNew.textTitle }}>{o.code}</Box>
								              <Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={o.name}>{o.name}</Typography>
							              </Box>
							              {reason && <Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber }}>{reason}</Typography>}
						              </Box>
					              </li>
				              )
			              }}
			              renderInput={(params) => <TextField {...params} placeholder={placeholder} error={!!why || error} sx={inputSx}/>}/>
			{why && <Typography sx={{ fontSize: 12, color: dsColorNew.dangerRed, mt: 0.5 }}>{why}</Typography>}
			{selected && !why && <Typography sx={{ ...subSx, mt: 0.5 }}>{selected.name}</Typography>}
		</Box>
	)
}

// The three counts beside the title, as the soft value chips of the app's dialogs
function Kpi({ label, value }) {
	return (
		<AppValueChip sx={{ py: 0.6, fontSize: 12.5 }}>
			<Box component="span" sx={{ color: dsColorNew.textMuted }}>{label}</Box>
			<Box component="span" sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{value}</Box>
		</AppValueChip>
	)
}
